import { Currency, CurrencyAmount, ETHER, JSBI, Token, TokenAmount } from '@uniswap/sdk'
import { useMemo, useEffect } from 'react'
import ERC20_INTERFACE from '../../constants/abis/erc20'
import { useAllTokens } from '../../hooks/Tokens'
import { useActiveWeb3React, ensureMetaMaskProvider } from '../../hooks'
import { useMulticallContract } from '../../hooks/useContract'
import { isAddress } from '../../utils'
import { useSingleContractMultipleData, useMultipleContractSingleData } from '../multicall/hooks'
import { storeWalletAddress } from '../../utils/walletStorage'

// 检查是否是网络变化错误
const isNetworkChangeError = (error: any): boolean => {
  return (
    error &&
    ((error.message &&
      (error.message.includes('underlying network changed') ||
        error.message.includes('NETWORK_ERROR') ||
        error.message.includes('network changed'))) ||
      error.code === 'NETWORK_ERROR' ||
      error.code === -32603)
  )
}

/**
 * Returns a map of the given addresses to their eventually consistent ETH balances.
 */
export function useETHBalances(
  uncheckedAddresses?: (string | undefined)[]
): { [address: string]: CurrencyAmount | undefined } {
  const multicallContract = useMulticallContract()
  const { chainId, library } = useActiveWeb3React()

  const addresses: string[] = useMemo(
    () =>
      uncheckedAddresses
        ? uncheckedAddresses
            .map(isAddress)
            .filter((a): a is string => a !== false)
            .sort()
        : [],
    [uncheckedAddresses]
  )

  // 确保 MetaMask provider 已激活
  useEffect(() => {
    if (window.ethereum && addresses.length > 0) {
      // 只激活 provider，不强制激活账户
      ensureMetaMaskProvider().catch(console.error)
    }
  }, [addresses.length]) // 只在地址数量变化时执行

  const results = useSingleContractMultipleData(
    multicallContract,
    'getEthBalance',
    addresses.map(address => [address])
  )

  return useMemo(
    () =>
      addresses.reduce<{ [address: string]: CurrencyAmount }>((memo, address, i) => {
        const result = results?.[i]

        // 检查是否是网络变化错误
        if (result?.error && isNetworkChangeError(result.error)) {
          console.debug('Network change error in ETH balance query, ignoring:', result.error)
          return memo
        }

        // 如果 multicall 失败，尝试直接调用 RPC
        if (library && result?.error) {
          library
            .getBalance(address)
            .then(balance => {
              // 存储钱包地址
              if (chainId) {
                storeWalletAddress(address, chainId)
              }
            })
            .catch(error => {
              // RPC 调用失败，可能是 MetaMask 权限问题，触发权限请求
              if (window.ethereum && error.code === -32603) {
                window.dispatchEvent(
                  new CustomEvent('requestMetaMaskPermission', {
                    detail: { address, chainId }
                  })
                )
              }
            })
        }

        const value = result?.result?.[0]
        if (value) {
          memo[address] = CurrencyAmount.ether(JSBI.BigInt(value.toString()))

          // 余额查询成功时存储钱包地址
          if (chainId) {
            storeWalletAddress(address, chainId)
          }
        }
        return memo
      }, {}),
    [addresses, results, chainId, library]
  )
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[]
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const { chainId } = useActiveWeb3React()
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [],
    [tokens]
  )

  const validatedTokenAddresses = useMemo(() => validatedTokens.map(vt => vt.address), [validatedTokens])

  // 确保 MetaMask provider 已激活
  useEffect(() => {
    if (window.ethereum && address && validatedTokenAddresses.length > 0) {
      // 只激活 provider，不强制激活账户
      ensureMetaMaskProvider().catch(console.error)
    }
  }, [address, validatedTokenAddresses.length]) // 只在地址或代币数量变化时执行

  const balances = useMultipleContractSingleData(validatedTokenAddresses, ERC20_INTERFACE, 'balanceOf', [address])

  const anyLoading: boolean = useMemo(() => balances.some(callState => callState.loading), [balances])

  return [
    useMemo(
      () =>
        address && validatedTokens.length > 0
          ? validatedTokens.reduce<{ [tokenAddress: string]: TokenAmount | undefined }>((memo, token, i) => {
              const result = balances?.[i]

              // 检查是否是网络变化错误
              if (result?.error && isNetworkChangeError(result.error)) {
                console.debug('Network change error in token balance query, ignoring:', result.error)
                return memo
              }

              const value = result?.result?.[0]
              const amount = value ? JSBI.BigInt(value.toString()) : undefined
              if (amount) {
                memo[token.address] = new TokenAmount(token, amount)

                // 代币余额查询成功时存储钱包地址
                if (chainId) {
                  storeWalletAddress(address, chainId)
                }
              }
              return memo
            }, {})
          : {},
      [address, validatedTokens, balances, chainId]
    ),
    anyLoading
  ]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[]
): { [tokenAddress: string]: TokenAmount | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): TokenAmount | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[]
): (CurrencyAmount | undefined)[] {
  const tokens = useMemo(() => currencies?.filter((currency): currency is Token => currency instanceof Token) ?? [], [
    currencies
  ])

  const tokenBalances = useTokenBalances(account, tokens)
  const containsETH: boolean = useMemo(() => currencies?.some(currency => currency === ETHER) ?? false, [currencies])
  const ethBalance = useETHBalances(containsETH ? [account] : [])

  return useMemo(
    () =>
      currencies?.map(currency => {
        if (!account || !currency) return undefined
        if (currency instanceof Token) return tokenBalances[currency.address]
        if (currency === ETHER) return ethBalance[account]
        return undefined
      }) ?? [],
    [account, currencies, ethBalance, tokenBalances]
  )
}

export function useCurrencyBalance(account?: string, currency?: Currency): CurrencyAmount | undefined {
  return useCurrencyBalances(account, [currency])[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: TokenAmount | undefined } {
  const { account } = useActiveWeb3React()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)

  // 过滤掉网络变化错误
  const filteredBalances = useMemo(() => {
    if (!balances) return {}

    const result: { [tokenAddress: string]: TokenAmount | undefined } = {}
    Object.keys(balances).forEach(tokenAddress => {
      const balance = balances[tokenAddress]
      if (balance) {
        result[tokenAddress] = balance
      }
    })
    return result
  }, [balances])

  return filteredBalances
}
